// Utilitários de formatação para componentes de drill down
export const formatCurrency = (value: number) => {

  if(typeof value !== 'number') {
    value = Number(value);
  };

  if(isNaN(value)) return '0,00';

  if(value === 0) return '0,00';

  if(value === Number.POSITIVE_INFINITY) return '0,00';

  if(value === Number.NEGATIVE_INFINITY) return '0,00';

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

export const formatNumber = (value: number | string) => {
  const numValue = Number(value);
  if (isNaN(numValue)) return '0';
  return numValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
};

export const formatQuantity = (value: number | string) => {
  const numValue = Number(value);
  if (isNaN(numValue)) return '0,00';
  return numValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const formatMonthYear = (mes: number, ano: number) => {
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
                     'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${monthNames[mes - 1]}/${ano}`;
};

export const formatYAxisValue = (value: number) => {
  const absValue = Math.abs(value);
  return absValue >= 1000000 
    ? `${(absValue / 1000000).toFixed(1)}M`
    : absValue >= 1000 
    ? `${(absValue / 1000).toFixed(1)}K`
    : absValue.toFixed(0);
};

// ===== UTILITÁRIOS PARA GRÁFICOS HORIZONTAIS =====

/**
 * Trunca texto para exibição em labels de gráficos
 * @param text Texto a ser truncado
 * @param maxLength Tamanho máximo (padrão 35 caracteres)
 * @returns Texto truncado com ellipsis se necessário
 */
export const truncateText = (text: string, maxLength: number = 35): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Formata labels para gráficos de barras horizontais
 * Garante que o texto inicie à esquerda e seja truncado adequadamente
 * @param text Texto original
 * @param maxLength Tamanho máximo para truncar
 * @returns Texto formatado
 */
export const formatHorizontalBarLabel = (text: string, maxLength: number = 35): string => {
  if (!text || typeof text !== 'string') return '';
  
  // Remove espaços extras e formata
  const cleanedText = text.trim().replace(/\s+/g, ' ');
  
  // Garante que o texto inicie à esquerda e seja truncado no final se necessário
  return truncateText(cleanedText, maxLength);
};

/**
 * Configurações padrão para gráficos de barras horizontais
 * Inclui margens, padding, espaçamento, legendas e tooltips otimizados
 */
export const getHorizontalBarChartConfig = (dataLength: number = 10, hasLongLabels: boolean = true) => {
  const baseHeight = Math.max(dataLength * 50, 350);
  const leftMargin = hasLongLabels ? 250 : 60; // Margem maior para labels longos
  
  return {
    height: baseHeight,
    margin: { 
      top: 0, 
      right: 10, 
      bottom: 30, // Margem ainda maior no bottom para legendas
      left: leftMargin 
    },
    padding: 0.35,
    innerPadding: 6,
    axisBottom: {
      tickSize: 5,
      tickPadding: 8, // Padding maior para evitar sobreposição
      tickRotation: 0, // Labels horizontais no eixo inferior
      legend: '',
      legendPosition: 'middle' as const,
      legendOffset: 60, // Offset maior para acomodar labels
      format: formatYAxisValue
    },
    axisLeft: {
      tickSize: 5,
      tickPadding: 8,
      tickRotation: 0
    },
    // Configurações otimizadas para legendas
    legends: {
      dataFrom: 'keys' as const,
      anchor: 'bottom' as const,
      direction: 'row' as const,
      justify: false, // Não distribuir - manter controle manual
      translateX: 0,
      translateY: 75, // Posição mais baixa para evitar sobreposição
      itemsSpacing: 60, // Espaçamento equilibrado entre itens completos
      itemWidth: 120, // Largura adequada (símbolo + texto próximos)
      itemHeight: 22, // Altura suficiente
      itemDirection: 'left-to-right' as const,
      itemOpacity: 1,
      symbolSize: 12, // Símbolo padrão, não muito grande
      symbolShape: 'circle' as const,
      symbolBorderColor: 'rgba(0, 0, 0, .3)',
      itemTextColor: 'inherit', // Usar cor do tema
      effects: [
        {
          on: 'hover' as const,
          style: {
            itemBackground: 'rgba(0, 0, 0, .05)',
            itemOpacity: 1
          }
        }
      ]
    },
    // Configurações CSS para containers permitirem tooltips externos
    containerStyles: {
      overflow: 'visible' as const,
      position: 'relative' as const,
      zIndex: 1
    }
  };
}; 